// eslint-disable-next-line import/no-cycle
import riskService from '@client/services/risk';
// eslint-disable-next-line import/no-cycle
import userService from '@client/services/user';
import Risk from '@client/models/store/risk';
import {TEMPLATES} from '@client/constants/email';
import {Logger, Category} from '@simpleinvest/front-end-logger';
import {displayErrorBanner} from '@client/events/notification-bus';
import {calculateStandardDeviation} from '@client/util/risk/calculateStandardDeviation';

const logger = new Logger({
  category: Category.RiskTolerance,
  fileName: 'risk.js',
});

const stateObj = () => new Risk();

const QUIZ_CHANGED_MSG =
  'The Risk Tolerance Questionnaire has been changed since the last time you saved it. Please start the new Risk Tolerance Questionnaire.';

const gettersObj = {
  computedScore: (state) => {
    if (state.isOverrideActive && Number.isInteger(state.overrideScore)) {
      return Number.parseInt(state.overrideScore, 10);
    }

    if (state.score) {
      return Number.parseInt(state.score, 10);
    }

    return null;
  },
  answerStandardDeviation: (state) => {
    // TODO - this map is needed to call std deviation - IN CDO.  may not be needed
    //        with RTQ Tab.  TBD
    const answers = state.questions
      .map((question) => {
        const selectedAnswer = question.answers.find((answer) => answer.selected);

        if (selectedAnswer) {
          return {risk_tolerance_answer_id: selectedAnswer.risk_tolerance_answer_id};
        }

        return null;
      })
      .filter((answer) => answer !== null);

    if (state.questions.length === 0) return 0;

    if (answers.length === state.questions.length) {
      return calculateStandardDeviation(state.questions, answers);
    }

    return 0;
  },
  computedScaleRange: (state, getters) => {
    const {computedScore} = getters;
    let rtqScaleRange = null;

    if (state.scaleRange) {
      rtqScaleRange = state.scaleRange.find(
        (scaleRangeSection) => computedScore <= scaleRangeSection.scoreMax && computedScore >= scaleRangeSection.scoreMin
      );
    }

    return rtqScaleRange || null;
  },
  computedRiskLabel: (_state, getters) => getters.computedScaleRange?.label,
  computedRiskDescription: (_state, getters) => getters.computedScaleRange?.description,
  computedRiskShortDescription: (_state, getters) => getters.computedScaleRange?.shortDescription,
  sliderScaleRange: (state) => {
    let rtqScaleRange = null;

    if (state.scaleRange && state.sliderScore) {
      rtqScaleRange = state.scaleRange.find(
        (scaleRangeSection) =>
          state.sliderScore <= scaleRangeSection.scoreMax && state.sliderScore >= scaleRangeSection.scoreMin
      );
    }

    return rtqScaleRange || null;
  },
  isOverrideActive: (state) => state.isOverrideActive,
  addAccountRetake: (state) => state.addAccountRetake,
  score: (state) => state.score,
  scaleRange: (state) => state.scaleRange,
  riskLabel: (state) => state.riskLabel,
  riskDescription: (state) => state.riskDescription,
  isRiskLoaded: (state) => state.isRiskLoaded,
};

const actions = {
  async retake({commit, dispatch, state, rootGetters}) {
    const clUserId = rootGetters['cognitoUser/userId'];

    await riskService.retakeRisk(clUserId);

    commit('SET_QUESTION_DATA', {
      questionnaire: {
        questions: [],
      },
      lastUpdate: state.lastUpdate,
      riskToleranceId: state.riskToleranceId,
      lastAnswered: 0,
    });

    commit('SET_COMPLETE', false);

    // NOTE: We are purposely not awaiting this call as a hack to work around a bug in the "PrincipalGauge".
    // There are two main timing issues with that component
    //  1. The underlying PrincipalChart component relies on reference to an HTML id.
    //     Rendering this id occurs asynchronously.
    //  2. The instantiation of a PrincipalChart is asynchronous. A few hundred milliseconds elapse between
    //     when the chart is created the the config object is available. The PrincipalGauge component is
    //     currently working around this by checking if the config object is present during each update event
    //     and reattempting to draw the chart.
    //
    // By removing the await, this hack essentially guarantees that the response of this API call happens after the
    // initial render. That triggers another render of the PrincipalGauge.
    //
    // Long-term we need to work with Principal to obtain a copy of the source code so we can add support for
    // promises.
    dispatch('getQuestions');
  },
  async getQuestions({commit, rootGetters}) {
    const userId = rootGetters['cognitoUser/userId'];
    const rtqData = await riskService.getRtqData(userId);
    const scaleRange = rtqData?.data?.data?.rtqInfo?.scaleRange;
    const questions = await riskService.getRTQQuestionnaire(userId);

    if (questions && Risk.hasQuizChanged(questions.msg)) {
      displayErrorBanner(QUIZ_CHANGED_MSG);
    } else {
      // eslint-disable-next-line no-lonely-if
      if (scaleRange !== null && questions && questions?.message !== 'Forbidden') {
        const payload = Risk.formatResponse(questions);

        commit('SET_QUESTION_DATA', payload);
        commit('SET_SCALE_RANGE', scaleRange);
      }
    }
  },
  selectAnswer({commit}, payload) {
    commit('SELECT_ANSWER', payload);
  },
  async saveAnswer({state, rootGetters}, questionIndex) {
    const selectedAnswer = state.questions[questionIndex].answers.find((answer) => answer.selected === true);
    const userId = rootGetters['cognitoUser/userId'];
    const response = await riskService.saveAnswer({
      questionnaire_lastupdate: state.lastUpdate,
      risk_tolerance_answer_id: selectedAnswer.risk_tolerance_answer_id,
      risk_tolerance_id: state.riskToleranceId,
      risk_tolerance_question_id: state.questions[questionIndex].risk_tolerance_question_id,
      cl_user_id: userId,
    });

    if (response?.status === 'success') {
      return response;
    }

    if (Risk.hasQuizChanged(response?.status)) {
      // this is for when an advisor has changed the quiz
      // while a client is filling it out
      displayErrorBanner(QUIZ_CHANGED_MSG);
      setTimeout(() => {
        // eslint-disable-next-line no-restricted-globals
        location.reload();
      }, 5000);

      return response;
    }

    throw new Error('There was a problem saving your answer.');
  },
  setOverrideScore({commit}, payload) {
    commit('SET_OVERRIDE_SCORE', payload);
  },
  setSliderScore({commit}, payload) {
    commit('SET_SLIDER_SCORE', payload);
  },

  async resetOverrideScore({commit, rootGetters, dispatch}) {
    const userId = rootGetters['cognitoUser/userId'];
    const response = await riskService.resetRiskOverride(userId);

    if (response?.status === 'success') {
      commit('SET_OVERRIDE_ACTIVE', false);
      commit('SET_OVERRIDE_SCORE', null);

      dispatch('overview/setRiskInfoDirty', null, {root: true});

      return response;
    }

    throw new Error('Reset override score failed');
  },
  // eslint-disable-next-line consistent-return
  async saveOverrideScore({commit, rootState, rootGetters, dispatch}) {
    try {
      if (rootState.risk.isOverrideActive) {
        const userId = rootGetters['cognitoUser/userId'];

        const response = await riskService.saveRiskOverride(userId, rootState.risk.sliderScore);

        if (response.status === 'success') {
          commit('SET_OVERRIDE_SCORE', rootState.risk.sliderScore);
          commit('SET_OVERRIDE_ACTIVE', true);

          dispatch('overview/setRiskInfoDirty', null, {root: true});

          return true;
        }

        return false;
      }
    } catch (error) {
      logger.addBreadcrumb({
        callingFuncName: 'saveOverrideScore',
        message: `saveOverrideScore error caught`,
      });
      logger.logException({error});

      return false;
    }
  },
  async getQuestionnaireScore({commit, rootGetters}) {
    const userId = rootGetters['cognitoUser/userId'];
    let scoreResult = null;

    try {
      scoreResult = await riskService.getQuestionnaireScore(userId);

      commit('SET_CURRENT_SCORE', scoreResult?.adjustedScore);
    } catch (error) {
      throw new Error(error.microserviceResponseMessage);
    }

    return scoreResult?.adjustedScore;
  },
  // eslint-disable-next-line consistent-return
  async saveQuestionnaireScore({dispatch, rootGetters}) {
    const userId = rootGetters['cognitoUser/userId'];
    const scoreResult = await riskService.saveQuestionnaireScore(userId);

    if (scoreResult) {
      await dispatch('fetchRtqData');

      dispatch('overview/setRiskInfoDirty', null, {root: true});

      return true;
    }
  },

  async sendAssessmentCompleteNotification({rootGetters}) {
    const clUserId = rootGetters['cognitoUser/userId'];
    const riaUserId = rootGetters['user/riaUserId'];
    const template = TEMPLATES.RISK_ASSESSMENT_COMPLETE;

    await userService.sendEmailToAdvisor({clUserId, riaUserId, template});
  },

  async sendScoreChangeNotification({rootGetters}, {previousScore, newScore}) {
    const clUserId = rootGetters['cognitoUser/userId'];
    const riaUserId = rootGetters['user/riaUserId'];
    const template = TEMPLATES.RISK_SCORE_CHANGED;
    const fields = {
      'previous-risk-score': previousScore,
      'risk-score': newScore,
    };

    await userService.sendEmailToAdvisor({clUserId, riaUserId, template}, fields);
  },
  setAddAccountRetake({commit}, payload) {
    commit('SET_ADD_ACCOUNT_RETAKE', payload);
  },
  // eslint-disable-next-line no-shadow, consistent-return
  async fetchRtqData({commit, getters, rootGetters}) {
    commit('SET_ID_RISK_LOADED', false);
    const userId = rootGetters['cognitoUser/userId'];

    try {
      const response = await riskService.getRtqData(userId);

      commit('SET_SCORE', response?.data.data?.rtqInfo?.score);
      commit('SET_SCALE_RANGE', response?.data.data?.rtqInfo?.scaleRange);
      commit('SET_COMPLETE', response?.data.data?.rtqInfo?.isComplete);
      commit('SET_OVERRIDE_ACTIVE', response?.data.data?.rtqInfo?.isOverride);
      commit('SET_OVERRIDE_SCORE', response?.data.data?.rtqInfo?.overrideScore);
      commit(
        'SET_SLIDER_SCORE',
        response?.data.data?.rtqInfo?.isOverride
          ? Number(response?.data.data?.rtqInfo?.overrideScore)
          : Number(response?.data.data?.rtqInfo?.score)
      );
      commit('SET_LAST_DATE', response?.data.data?.rtqInfo?.lastDate);
      commit('SET_RISK_LABEL', getters.computedScaleRange?.label);
      commit('SET_RISK_DESCRIPTION', getters.computedScaleRange?.description);
      commit('SET_ID_RISK_LOADED', true);
    } catch (error) {
      logger.addBreadcrumb({
        callingFuncName: 'getRtqData',
        message: 'getRtqData error caught',
      });
      logger.logException({
        error,
        fingerprint: ['getRtqData'],
      });
    }
  },
  resetOverrideActive({commit}, payload) {
    commit('SET_OVERRIDE_ACTIVE', payload);
  },
};

const mutations = {
  SET_QUESTION_DATA(state, data) {
    state.questions = data.questionnaire.questions;
    state.lastUpdate = data.questionnaire.lastUpdate;
    state.riskToleranceId = data.questionnaire.riskToleranceId;

    if (data.lastAnswered !== null) {
      state.gotoQuestion = data.lastAnswered;
    }
  },
  SELECT_ANSWER(state, payload) {
    state.questions[payload.questionIndex].answers.forEach((answer) => {
      // eslint-disable-next-line no-param-reassign
      answer.selected = false;
    });
    state.questions[payload.questionIndex].answers[payload.answerIndex].selected = true;
  },
  SET_COMPLETE(state, payload) {
    state.isComplete = payload;
  },
  SET_SCORE(state, payload) {
    state.score = payload ? Number.parseInt(payload, 10) : payload;
  },
  SET_SCALE_RANGE(state, payload) {
    state.scaleRange = payload;
  },
  SET_OVERRIDE_SCORE(state, payload) {
    if (payload !== null) {
      // eslint-disable-next-line radix, no-param-reassign
      payload = Number.parseInt(payload);
    }

    state.overrideScore = payload;
  },
  SET_OVERRIDE_ACTIVE(state, payload) {
    state.isOverrideActive = payload;
  },
  SET_LAST_DATE(state, payload) {
    state.lastScoreChange = payload;
  },
  SET_SLIDER_SCORE(state, payload) {
    state.sliderScore = payload;
  },
  SET_CURRENT_SCORE(state, payload) {
    state.currentQuestionnaireScore = payload;
  },
  SET_ADD_ACCOUNT_RETAKE(state, payload) {
    state.addAccountRetake = payload;
  },
  SET_RISK_LABEL(state, payload) {
    state.riskLabel = payload;
  },
  SET_RISK_DESCRIPTION(state, payload) {
    state.riskDescription = payload;
  },
  SET_ID_RISK_LOADED(state, payload) {
    state.isRiskLoaded = payload;
  },
};

export default {
  namespaced: true,
  state: stateObj,
  getters: gettersObj,
  actions,
  mutations,
};
